import NewNotesButton from '@/components/NewNotesButton'

import { Button } from '@/components/ui/button'
import { isReplyNoteEvent } from '@/lib/event'
import { batchDebounce, isTouchDevice, now } from '@/lib/utils'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import {
  getSeenEventRelays,
  loadMoreTimeline,
  onEventRemoved,
  subscribeTimeline,
  trackEventSeenOn
} from '@/services/client.service'
import { TFeedSubRequest } from '@/types'
import { Event, NostrEvent, verifyEvent } from '@nostr/tools/wasm'
import { mergeReverseSortedLists } from '@nostr/tools/utils'
import * as kinds from '@nostr/tools/kinds'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { toast } from 'sonner'
import NoteCard, { NoteCardLoadingSkeleton } from './NoteCard'
import { ShortNoteView, ShortNoteViewSkeleton } from './ShortNoteView'
import PinnedNoteCard from './PinnedNoteCard'
import { includeRepliesAtom } from '@/atoms/feed'
import { noteListStyleAtom } from '@/atoms/preferences'
import { useCurrentPubkey } from '@/atoms/session'

const LIMIT = 200
const DEFAULT_SHOW_COUNT = 10

function getShowCount(noteListStyle: string): number {
  if (noteListStyle === 'compact') return 25
  if (noteListStyle === 'detailed') return 18
  return DEFAULT_SHOW_COUNT
}

const NoteList = forwardRef(
  (
    {
      subRequests,
      showKinds,
      filterMutedNotes = true,
      showRelayCloseReason = false,

      sinceTimestamp,
      onNotesLoaded,
      pinnedEventIds,
      filterFn,
      customFilteredFooter
    }: {
      subRequests: null | TFeedSubRequest[]
      showKinds: number[]
      filterMutedNotes?: boolean
      showRelayCloseReason?: boolean
      sinceTimestamp?: number
      onNotesLoaded?: (count: number, hasPosts: boolean, hasReplies: boolean) => void
      pinnedEventIds?: string[]
      filterFn?: (event: Event) => boolean
      customFilteredFooter?: React.ReactNode
    },
    ref
  ) => {
    const { t } = useTranslation()
    const pubkey = useCurrentPubkey()
    const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
    const noteListStyle = useAtomValue(noteListStyleAtom)
    const pageSize = getShowCount(noteListStyle)
    const [events, setEvents] = useState<Event[]>([])
    const [newEvents, setNewEvents] = useState<Event[]>([])
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [loading, setLoading] = useState(true)
    const [removingIds, setRemovingIds] = useState<Set<string>>(new Set())
    const [refreshCount, setRefreshCount] = useState(0)
    const [showCount, setShowCount] = useState(pageSize)
    const [isFilteredView, setIsFilteredView] = useState(!!sinceTimestamp)
    const includeReplies = useAtomValue(includeRepliesAtom)
    const supportTouch = useMemo(() => isTouchDevice(), [])
    const bottomRef = useRef<HTMLDivElement | null>(null)
    const topRef = useRef<HTMLDivElement | null>(null)

    // refs kept in sync with the latest committed values so that debounced/async
    // callbacks (handleNew, loadMore) and the IntersectionObserver always read
    // fresh state without needing to be recreated
    const eventsRef = useRef(events)
    const loadingRef = useRef(loading)
    const isFilteredViewRef = useRef(isFilteredView)
    const sinceTimestampRef = useRef(sinceTimestamp)
    const pubkeyRef = useRef(pubkey)
    const loadMoreRef = useRef<() => void>(() => {})
    eventsRef.current = events
    loadingRef.current = loading
    isFilteredViewRef.current = isFilteredView
    sinceTimestampRef.current = sinceTimestamp
    pubkeyRef.current = pubkey

    const shouldHideEvent = useCallback(
      (evt: Event) => {
        if (pinnedEventIds && pinnedEventIds.includes(evt.id)) return true
        if (!includeReplies && isReplyNoteEvent(evt)) return true
        if (filterMutedNotes && mutePubkeySet.has(evt.pubkey)) return true
        if (filterFn && !filterFn(evt)) {
          return true
        }

        return false
      },
      [mutePubkeySet, pinnedEventIds, filterFn, includeReplies]
    )
    const shouldHideRef = useRef(shouldHideEvent)
    shouldHideRef.current = shouldHideEvent

    const filteredEvents = useMemo(() => {
      const repostersMap = new Map<string, { reposters: string[]; repostEvent: NostrEvent }>()
      const filteredEvents: { event: NostrEvent; reposters: string[]; repostEvent?: NostrEvent }[] =
        []

      // when in filtered view, show all loaded events; otherwise use showCount limit
      const displayLimit = isFilteredView ? events.length : Math.min(events.length, showCount)

      for (let i = 0; i < displayLimit; i++) {
        const event = events[i]

        if (shouldHideEvent(event)) continue

        if (event.kind !== kinds.Repost && event.kind !== kinds.GenericRepost) {
          // for all events just stop processing here, this is it
          filteredEvents.push({ event, reposters: [] })
          continue
        }

        // except reposts, for these we will do some grouping
        let eventFromContent: NostrEvent
        try {
          eventFromContent = JSON.parse(event.content) as NostrEvent
        } catch (_err) {
          // generic reposts may carry no embedded event; show the repost itself
          filteredEvents.push({ event, reposters: [event.pubkey], repostEvent: event })
          continue
        }

        // before we verify anything let's check if we have already seen this
        let existing = repostersMap.get(eventFromContent.id)
        if (!existing) {
          // we haven't seen it:
          if (shouldHideEvent(eventFromContent)) continue
          if (!verifyEvent(eventFromContent)) {
            filteredEvents.push({ event, reposters: [event.pubkey], repostEvent: event })
            continue
          }

          const targetSeenOn = getSeenEventRelays(eventFromContent.id)
          if (targetSeenOn.length === 0) {
            const seenOn = getSeenEventRelays(event.id)
            for (const relay of seenOn) {
              trackEventSeenOn(eventFromContent.id, relay)
            }
          }

          existing = { reposters: [], repostEvent: event }
          filteredEvents.push({ event: eventFromContent, ...existing })
        }

        // now that we have it all set up and this repost added to the list add the current reposter
        if (!existing.reposters.includes(event.pubkey)) {
          existing.reposters.push(event.pubkey)
        }
        repostersMap.set(eventFromContent.id, existing)
      }

      return filteredEvents
    }, [events, showCount, shouldHideEvent, isFilteredView])

    useEffect(() => {
      function handler(eventId: string) {
        setRemovingIds((prev) => new Set(prev).add(eventId))

        setTimeout(() => {
          setEvents((events) => events.filter((e) => e.id !== eventId))
          setNewEvents((events) => events.filter((e) => e.id !== eventId))
          setRemovingIds((prev) => {
            const next = new Set(prev)
            next.delete(eventId)
            return next
          })
        }, 300)
      }

      return onEventRemoved(handler)
    }, [])

    useImperativeHandle(ref, () => ({ scrollToTop, refresh }), [])

    useEffect(() => {
      if (!subRequests || !subRequests.length) return

      setLoading(true)
      setEvents([])
      setNewEvents([])
      setHasMore(true)

      if (showKinds.length === 0) {
        setLoading(false)
        setHasMore(false)
        return () => {}
      }

      const subc = subscribeTimeline(
        subRequests,
        {
          kinds: showKinds,
          limit: LIMIT,
          ...(sinceTimestamp && isFilteredView ? { since: sinceTimestamp } : {})
        },
        {
          async onEvents(events, isFinal) {
            if (isFinal) {
              setLoading(false)
              setHasMore(events.length > 0)

              if (onNotesLoaded) {
                // notify parent about notes composition (notes vs replies)
                let hasPosts = false
                let hasReplies = false
                for (let i = 0; i < events.length; i++) {
                  if (isReplyNoteEvent(events[i])) {
                    hasReplies = true
                  } else {
                    hasPosts = true
                  }
                  if (hasReplies && hasPosts) break
                }
                onNotesLoaded(events.length, hasPosts, hasReplies)
              }
            }

            if (events.length > 0) {
              setEvents(events)
            }
          },
          onNew: handleNew,
          onClose(url, reason) {
            if (!showRelayCloseReason) return
            // ignore reasons from @nostr/tools
            if (
              [
                'closed by caller',
                'relay connection errored',
                'relay connection closed',
                'pingpong timed out',
                'relay connection closed by us'
              ].includes(reason)
            ) {
              return
            }

            console.warn(`${url}: ${reason}`)
            toast.warning(`${url}: ${reason}`)
          }
        }
      )

      return () => {
        handleNew.cancel()
        subc.close()
      }
    }, [subRequests, refreshCount, showKinds])

    // one stable debounced instance for the whole lifetime of the component.
    // it reads the latest state through refs so it never goes stale even though
    // the live subscription that calls it is only re-created on refresh.
    const handleNew = useMemo(
      () =>
        batchDebounce(
          (newEvents: NostrEvent[]) => {
            const events = eventsRef.current
            const pubkey = pubkeyRef.current
            const shouldHideEvent = shouldHideRef.current

            let pending: Event[] = []
            const appended: Event[] = []

            for (let i = 0; i < newEvents.length; i++) {
              const newEvent = newEvents[i]

              // TODO: figure out where exactly the viewport is: for now just assume it's at the top
              if (events.length < 7 || newEvent.created_at < events[6].created_at) {
                // if there are very few events in the viewport or the new events would be inserted below, just append
                appended.push(newEvent)
              } else if (pubkey && newEvent.pubkey === pubkey) {
                // our own notes are also inserted regardless of any concern
                appended.push(newEvent)
              } else if (shouldHideEvent(newEvent)) {
                // filtered-out notes are stored silently so they appear if the filter changes
                appended.push(newEvent)
              } else {
                // any other "new" notes that would be inserted above, make them be pending in the modal thingie
                pending.push(newEvent)
              }
            }

            if (pending.length > 0 && pending.length <= 3) {
              // few new notes, check before if they will be merged or if they are duplicates
              pending = pending.filter(
                (p) => !newEvents.find((n) => n.id === p.id) && !events.find((e) => e.id === p.id)
              )
            }

            if (pending.length) {
              // sort these as they will not come in order (they will come from different author syncing processes)
              pending.sort((a, b) => b.created_at - a.created_at)
              // prepend them to the top
              setNewEvents((curr) =>
                [...pending, ...curr].sort((a, b) => b.created_at - a.created_at)
              )
            }

            // insert each appended event using binary search to maintain sort order and deduplicate
            if (appended.length) {
              setEvents((events) =>
                mergeReverseSortedLists(
                  events,
                  appended.sort((a, b) => b.created_at - a.created_at)
                )
              )
            }
          },
          1800,
          3000
        ),
      []
    )

    const loadMore = useCallback(async () => {
      if (!subRequests || subRequests.length === 0) return

      // read the latest committed state through refs so this callback stays
      // correct without being recreated (and so it can be invoked via a ref
      // from the IntersectionObserver / "Load more notes" button)
      if (loadingRef.current) return

      const events = eventsRef.current

      if (showCount < events.length) {
        setShowCount((prev) => prev + pageSize)
        // do we need to preload more?
        if (events.length - showCount > LIMIT / 2) {
          // no, we don't
          return
        }
      }

      // set the ref synchronously so re-entrant calls (e.g. repeated observer
      // firings before the next render) are blocked by the guard above
      loadingRef.current = true
      setLoading(true)

      let moreEvents: NostrEvent[]
      try {
        moreEvents = await loadMoreTimeline(subRequests, {
          until: events.length ? events[events.length - 1].created_at - 1 : now(),
          limit: LIMIT,
          ...(sinceTimestampRef.current && isFilteredViewRef.current
            ? { since: sinceTimestampRef.current }
            : {})
        })
      } finally {
        // always release the guard so a rejected request doesn't wedge loading
        loadingRef.current = false
        setLoading(false)
      }

      if (moreEvents.length === 0) {
        // we have nothing more to load
        setHasMore(false)
        return
      }

      // dedup and keep reverse-sorted order; sort defensively because the
      // single-relay branch of loadMoreTimeline may return unsorted results
      // and mergeReverseSortedLists requires both inputs reverse-sorted
      setEvents((events) =>
        mergeReverseSortedLists(
          events,
          moreEvents.sort((a, b) => b.created_at - a.created_at)
        )
      )
    }, [showCount, subRequests, pageSize])
    loadMoreRef.current = loadMore

    useEffect(() => {
      setShowCount(pageSize)
    }, [pageSize])

    useEffect(() => {
      if (!hasMore || loading || isFilteredView) return

      const observerInstance = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreRef.current?.()
          }
        },
        {
          root: null,
          rootMargin: '10px',
          threshold: 0.1
        }
      )

      const currentBottomRef = bottomRef.current

      if (currentBottomRef) {
        observerInstance.observe(currentBottomRef)
      }

      return () => {
        if (observerInstance && currentBottomRef) {
          observerInstance.unobserve(currentBottomRef)
        }
      }
    }, [hasMore, loading, isFilteredView])

    function mergeNewEvents() {
      setEvents((oldEvents) =>
        mergeReverseSortedLists(
          oldEvents,
          newEvents.sort((a, b) => b.created_at - a.created_at)
        )
      )
      setNewEvents([])
      setTimeout(() => {
        scrollToTop('smooth')
      }, 0)
    }

    const list = (
      <div className="min-h-screen">
        {noteListStyle === 'compact' || noteListStyle === 'detailed' ? (
          <>
            {filteredEvents.map(({ event, repostEvent }) => (
              <div
                key={`d/c-${event.id}:${repostEvent?.id}`}
                className={'transition-all duration-300 ease-out'.concat(
                  removingIds.has(event.id) ? ' opacity-0 scale-95' : ''
                )}
              >
                <ShortNoteView
                  event={event}
                  style={noteListStyle}
                  isRepostFrom={repostEvent?.pubkey}
                />
              </div>
            ))}
          </>
        ) : (
          <>
            {(pinnedEventIds || []).map((id) => (
              <PinnedNoteCard key={`p-${id}`} eventId={id} className="w-full" />
            ))}

            {filteredEvents.map(({ event, reposters, repostEvent }) => (
              <div
                key={`m-${event.id}:${/* this differentiates between reposts and direct notes: */ repostEvent?.id}`}
                className={'transition-all duration-300 ease-out'.concat(
                  removingIds.has(event.id) ? ' opacity-0 scale-95' : ''
                )}
              >
                <NoteCard
                  className="w-full"
                  event={event}
                  filterMutedNotes={filterMutedNotes}
                  reposters={reposters}
                  repostEvent={repostEvent}
                  clamp
                />
              </div>
            ))}
          </>
        )}
        {isFilteredView && events.length > 0 && !loading ? (
          customFilteredFooter ? (
            customFilteredFooter
          ) : (
            <div className="flex justify-center items-center mt-4 p-4">
              <Button
                size="lg"
                onClick={async () => {
                  setIsFilteredView(false)
                  // update the ref synchronously so the upcoming loadMore drops
                  // the `since` window and can fetch older notes
                  isFilteredViewRef.current = false
                  setHasMore(true)
                  // Give React a moment to update state, then trigger load
                  setTimeout(() => loadMoreRef.current?.(), 0)
                }}
              >
                {t('Load more notes')}
              </Button>
            </div>
          )
        ) : hasMore || loading ? (
          <div ref={bottomRef}>
            {noteListStyle === 'compact' || noteListStyle === 'detailed' ? (
              <ShortNoteViewSkeleton style={noteListStyle} />
            ) : (
              <NoteCardLoadingSkeleton />
            )}
          </div>
        ) : events.length && !hasMore ? (
          <div className="text-center text-sm text-muted-foreground mt-2">{t('no more notes')}</div>
        ) : !loading && !events.length ? (
          <div className="flex justify-center w-full mt-2">
            <Button size="lg" onClick={() => setRefreshCount((count) => count + 1)}>
              {t('reload notes')}
            </Button>
          </div>
        ) : null}
      </div>
    )

    return (
      <div>
        <div ref={topRef} className="scroll-mt-[calc(6rem+1px)]" />
        {supportTouch ? (
          <PullToRefresh
            onRefresh={async () => {
              refresh()
              await new Promise((resolve) => setTimeout(resolve, 1000))
            }}
            pullingContent=""
          >
            {list}
          </PullToRefresh>
        ) : (
          list
        )}
        <div className="h-40" />
        {newEvents.length > 0 && <NewNotesButton newEvents={newEvents} onClick={mergeNewEvents} />}
      </div>
    )

    function scrollToTop(behavior: ScrollBehavior = 'instant') {
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior, block: 'start' })
      }, 20)
    }

    function refresh() {
      scrollToTop()
      setTimeout(() => {
        setRefreshCount((count) => count + 1)
      }, 500)
    }
  }
)

NoteList.displayName = 'NoteList'
export default NoteList

export type TNoteListRef = {
  scrollToTop: (behavior?: ScrollBehavior) => void
  refresh: () => void
}
